import React from 'react';
import { format, isToday, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const MonthView = ({
  currentDate,
  days,
  emptyDays,
  monthAppointments,
  selectedDate,
  onDaySelect,
  onAppointmentClick,
  onPrevMonth,
  onNextMonth,
  onToday
}) => {
  return (
    <Card className="bg-white border-slate-100 shadow-soft">
      <CardHeader className="pb-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-brand-main" strokeWidth={1.5} />
            {format(currentDate, 'MMMM yyyy', { locale: es })}
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={onPrevMonth}
              className="border-slate-200 hover:bg-slate-100"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onToday}
              className="border-slate-200 hover:bg-slate-100"
            >
              Hoy
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={onNextMonth}
              className="border-slate-200 hover:bg-slate-100"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
            <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid with appointments */}
        <div className="grid grid-cols-7 gap-2">
          {emptyDays.map((_, index) => (
            <div key={`empty-${index}`} className="min-h-[120px]" />
          ))}
          {days.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayAppts = monthAppointments[dateStr] || [];
            const isDayToday = isToday(day);
            const isSelected = isSameDay(day, selectedDate);
            
            return (
              <div 
                key={dateStr}
                className={`min-h-[120px] rounded-lg border p-1 transition-all ${
                  isDayToday 
                    ? 'border-[#00b4d8] bg-[#00b4d8]/5' 
                    : isSelected 
                    ? 'border-[#1a5192] bg-[#1a5192]/5' 
                    : 'border-slate-100 hover:border-slate-200'
                }`}
              >
                <div 
                  className="flex items-center justify-between cursor-pointer mb-1"
                  onClick={() => onDaySelect(day)}
                >
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium ${
                    isDayToday 
                      ? 'bg-[#00b4d8] text-white' 
                      : isSelected
                      ? 'bg-[#1a5192] text-white'
                      : ''
                  }`}>
                    {format(day, 'd')}
                  </span>
                  {dayAppts.length > 0 && (
                    <Badge className="text-[10px] bg-[#1a5192]/10 text-[#1a5192] px-1">
                      {dayAppts.length}
                    </Badge>
                  )}
                </div>
                <div className="space-y-0.5 max-h-[85px] overflow-y-auto">
                  {dayAppts.slice(0, 3).map((appt) => (
                    <div 
                      key={appt.id}
                      onClick={() => onAppointmentClick(appt)}
                      className={`px-1 py-0.5 rounded text-[10px] cursor-pointer truncate ${
                        appt.status === 'COMPLETADA' ? 'bg-emerald-100 text-emerald-700' :
                        appt.status === 'CANCELADA' ? 'bg-red-100 text-red-700 line-through' :
                        appt.status === 'NO_ASISTIO' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}
                    >
                      <span className="font-medium">{appt.hora_inicio}</span> {appt.patient_name}
                    </div>
                  ))}
                  {dayAppts.length > 3 && (
                    <p className="text-[10px] text-slate-400 text-center">
                      +{dayAppts.length - 3} más
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthView;
